<template>
  <div>
    <label class="block text-sm font-medium text-gray-700 mb-1">
      Attachments
      <span class="text-gray-600 text-xs">(Optional, max 3 files, 5MB each)</span>
    </label>

    <div class="space-y-2">
      <!-- File input -->
      <input
        type="file"
        ref="fileInput"
        multiple
        :accept="acceptedTypes"
        @change="handleFileChange"
        class="block w-full text-sm text-gray-600
          file:mr-4 file:py-2 file:px-4
          file:rounded file:border-0
          file:text-sm file:font-semibold
          file:bg-black file:text-white
          hover:file:bg-koshee-orange
          transition-all
          disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="props.disabled || files.length >= 3"
        aria-describedby="file-upload-help"
      />

      <p id="file-upload-help" class="text-xs text-gray-600">
        Supported: PDF, Images (JPG, PNG), Documents (DOC, DOCX)
      </p>

      <!-- Error message -->
      <p v-if="error" class="text-sm text-red-600" role="alert">
        {{ error }}
      </p>

      <!-- Selected files list -->
      <ul v-if="files.length > 0" class="space-y-1">
        <li
          v-for="(file, index) in files"
          :key="index"
          class="flex items-center justify-between text-sm bg-gray-50 rounded-none px-3 py-2 border border-gray-200"
        >
          <span class="flex-1 truncate text-gray-700">
            {{ file.filename }} <span class="text-gray-500">({{ formatSize(file.size) }})</span>
          </span>
          <button
            type="button"
            @click="removeFile(index)"
            class="ml-2 text-koshee-orange hover:text-red-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            :aria-label="`Remove ${file.filename}`"
            :disabled="props.disabled"
          >
            ✕
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import type { AttachmentRef } from "@hubspot-ticket/shared";

const MAX_FILES = 3;
const MAX_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_TYPES = ".pdf,.jpg,.jpeg,.png,.doc,.docx";

const props = defineProps<{
  modelValue?: AttachmentRef[];
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (event: "update:modelValue", files: AttachmentRef[]): void;
}>();

const fileInput = ref<HTMLInputElement>();
const files = ref<AttachmentRef[]>(props.modelValue || []);
const error = ref<string>("");
const acceptedTypes = ACCEPTED_TYPES;

watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    files.value = newValue;
  }
});

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const handleFileChange = async (event: Event) => {
  error.value = "";
  const input = event.target as HTMLInputElement;
  const selectedFiles = Array.from(input.files || []);

  // Validate file count
  if (files.value.length + selectedFiles.length > MAX_FILES) {
    error.value = `Maximum ${MAX_FILES} files allowed`;
    input.value = "";
    return;
  }

  // Validate and process each file
  for (const file of selectedFiles) {
    // Validate size
    if (file.size > MAX_SIZE) {
      error.value = `File "${file.name}" exceeds 5MB limit`;
      input.value = "";
      return;
    }

    // Validate type
    const ext = `.${file.name.split(".").pop()?.toLowerCase()}`;
    if (!ACCEPTED_TYPES.includes(ext)) {
      error.value = `File type "${ext}" not supported`;
      input.value = "";
      return;
    }

    try {
      const data = await fileToBase64(file);
      files.value.push({
        filename: file.name,
        contentType: file.type,
        size: file.size,
        data
      });
    } catch (err) {
      error.value = `Failed to process file "${file.name}"`;
      input.value = "";
      return;
    }
  }

  emit("update:modelValue", files.value);
  input.value = ""; // Reset input
};

const removeFile = (index: number) => {
  files.value.splice(index, 1);
  emit("update:modelValue", files.value);
  error.value = "";
};
</script>
